// ==========================================
// COMERCIAL INTERNO (set/2026) — plano por desconto direto, sem público
// ==========================================
// Modelo aprovado com o dono do produto em 09/09/2026, hoje só para a Júlia
// Cintra (departamento Comercial Interno — ver ehComercialInterno). Substitui
// o pacote de planos × público do vendedor externo inteiro: aqui o vendedor
// escolhe o desconto de tarifa (20% a 30%, de 1 em 1) e o de bandeira (0/50/100%)
// direto, e a comissão sai de tabela — sem "plano_codigo" nem classificação por
// Público A/B/C (essa é outra grandeza: tamanho da UC do cliente, não tem mais
// papel nenhum aqui).
//
// Fonte única: modal Alterar Plano (isVendedorInterno), etapa "Definição do
// Plano" da Nova Negociação, e o motor de fechamento (comissaoInternaV1.ts).
//
// Arquivo separado de politicaComercial.ts DE PROPÓSITO: aquele arquivo importa
// o cliente supabase no topo (usado só por 2 das ~15 funções de lá), e isso
// derruba qualquer teste que importe até uma função 100% pura dali — import.meta.env
// não existe fora do Vite. Este arquivo não tem nenhuma dependência de banco,
// então dá pra testar a fórmula (a lógica de maior risco: é dinheiro) direto
// com node:test, sem servidor nenhum no meio.
import { getFatorFidelidade } from './politicaComercial.ts';
import type { GrupoAStatus } from '../../types';
import { montarRepasse } from './comissaoIndicacaoPosVenda.ts';
import type { RepassePosVenda } from './comissaoIndicacaoPosVenda.ts';

// A Júlia Cintra tem registros em comissao_vendedores de fev a jun/2026, todos
// `politica: 'v1'` e — importante — todos em status 'calculado' (nunca
// aprovados nem pagos). O auto-cálculo da Folha de Pagamento RECALCULA
// registros 'calculado' toda vez que a tela é aberta naquele mês (só pula
// 'aprovado'/'pago'). Sem esta virada, o motor novo hijackaria retroativamente
// meses em que ela era legitimamente paga pelo modelo antigo — mesmo cuidado
// que isCompetenciaNovaPolitica já tinha pro vendedor externo (jul/2026).
// Escolhida como ago/2026 porque é o primeiro mês SEM registro nenhum: não há
// nada legítimo pra proteger a partir daí, e é o mês que a simulação
// apresentada ao dono do produto em 09/09/2026 já assume.
export const COMPETENCIA_VIRADA_INTERNO = { mes: 8, ano: 2026 } as const;

export function isCompetenciaNovaPoliticaInterno(mes: number, ano: number): boolean {
  return ano > COMPETENCIA_VIRADA_INTERNO.ano ||
    (ano === COMPETENCIA_VIRADA_INTERNO.ano && mes >= COMPETENCIA_VIRADA_INTERNO.mes);
}

export const INTERNO_TARIFA_MIN = 20;
export const INTERNO_TARIFA_MAX = 30;
export const INTERNO_BANDEIRAS = [0, 50, 100] as const;
export type InternoBandeira = typeof INTERNO_BANDEIRAS[number];

/** Degraus de 1% de 20 a 30 — as mesmas 11 opções da tabela aprovada. */
export const INTERNO_TARIFAS_SELECIONAVEIS: number[] = Array.from(
  { length: INTERNO_TARIFA_MAX - INTERNO_TARIFA_MIN + 1 },
  (_, i) => INTERNO_TARIFA_MIN + i
);

/**
 * % de comissão da tabela aprovada: cada +1% de desconto na tarifa tira 0,5 p.p.
 * de comissão; cada degrau de bandeira (0→50→100) tira outros 0,5 p.p. — padrão
 * já embutido na tabela original (20%/50%→12,5%, 20%/100%→12,0%, 21%/50%→12,0%...).
 *
 * A coluna de bandeira 0% NÃO veio na tabela original — é extrapolação desta
 * mesma progressão (mais 0,5 p.p. acima da coluna de 50%), proposta e validada
 * com o dono do produto em 09/09/2026.
 *
 * Fora da faixa 20–30 (negociação "fora da política"), a fórmula continua
 * valendo por extrapolação linear — não é usada pra decidir se a venda pode
 * ser feita (isso é isDentroPoliticaInterno + aprovação do gerente), só pra dar
 * um número sensato de comissão a uma condição excepcional já aprovada.
 */
export function getComissaoInternoTabela(descTarifa: number, descBandeira: number): number {
  const degrauBandeira = descBandeira <= 0 ? 0 : descBandeira <= 50 ? 1 : 2;
  return 13.0 - 0.5 * (descTarifa - INTERNO_TARIFA_MIN) - 0.5 * degrauBandeira;
}

/** Dentro da tabela aprovada (20–30% tarifa, bandeira 0/50/100)? Fora disso
 *  exige aprovação do gerente — mesmo espírito do "fora da política" do
 *  vendedor externo, só que sem plano_codigo pra comparar (ver discountApproval). */
export function isDentroPoliticaInterno(descTarifa: number, descBandeira: number): boolean {
  return descTarifa >= INTERNO_TARIFA_MIN && descTarifa <= INTERNO_TARIFA_MAX
    && (INTERNO_BANDEIRAS as readonly number[]).includes(descBandeira);
}

// ── Pilares do multiplicador (0,30x a 1,80x) ────────────────────────────────
// Proposta original (09/09/2026): "vamos seguir com a sua proposta por ora" —
// os LIMIARES abaixo são a primeira versão, não uma régua fechada com a
// diretoria. A arquitetura (3 pilares multiplicativos, teto/piso 0,30x–1,80x)
// é a parte estável; ajustar valor de faixa aqui é seguro.

/** Volume: atingimento da meta mensal da vendedora (a meta por pessoa da Folha
 *  de Pagamento, comissao_vendedores.meta_kwh — ver src/lib/metasComerciais.ts) — vale por igual pra TODAS as negociações
 *  fechadas no mês, não por negociação. É o único pilar que pode cair por não
 *  bater meta — por isso o bônus de prospecção ativa fica de fora dele
 *  (ver INTERNO_BONUS_PROSPECCAO_PP). */
export const INTERNO_VOLUME_FAIXAS = [
  { minPct: 0, mult: 0.70, label: 'Abaixo de 70% da meta' },
  { minPct: 70, mult: 0.90, label: '70% a 99% da meta' },
  { minPct: 100, mult: 1.00, label: '100% a 119% da meta (bateu a meta)' },
  { minPct: 120, mult: 1.15, label: '120% a 149% da meta' },
  { minPct: 150, mult: 1.30, label: '150% da meta ou mais' },
] as const;

export function getInternoMultiplicadorVolume(pctAtingimentoMeta: number): number {
  let mult: number = INTERNO_VOLUME_FAIXAS[0].mult;
  for (const f of INTERNO_VOLUME_FAIXAS) if (pctAtingimentoMeta >= f.minPct) mult = f.mult;
  return mult;
}

/** Desconto: faixa do desconto NA TARIFA desta negociação. A bandeira já pesa
 *  no % base da tabela (getComissaoInternoTabela) — reentrar aqui contaria a
 *  mesma alavanca duas vezes, por isso só tarifa. */
export const INTERNO_DESCONTO_FAIXAS = [
  { max: 22, mult: 1.15, label: '20% a 22%' },
  { max: 25, mult: 1.00, label: '23% a 25%' },
  { max: 28, mult: 0.90, label: '26% a 28%' },
  { max: 30, mult: 0.80, label: '29% a 30%' },
  { max: Infinity, mult: 0.50, label: 'Fora da política (acima de 30%)' },
] as const;

export function getInternoMultiplicadorDesconto(descTarifa: number): number {
  const faixa = INTERNO_DESCONTO_FAIXAS.find(f => descTarifa <= f.max);
  return (faixa ?? INTERNO_DESCONTO_FAIXAS[INTERNO_DESCONTO_FAIXAS.length - 1]).mult;
}

export const INTERNO_MULT_MIN = 0.30;
export const INTERNO_MULT_MAX = 1.80;

/** Fidelidade reaproveita getFatorFidelidade com planoCodigo=undefined — cai na
 *  escala padrão (0,90 / 1,00 / 1,10 / 1,20), a mesma que o Comercial Interno
 *  já usa hoje na tela de plano. Não existe plano_codigo aqui, então a variante
 *  fidelidadeBinaria nunca entra em jogo. */
export function getInternoMultiplicadorFinal(multVolume: number, fatorFidelidade: number, multDesconto: number): number {
  return Math.min(INTERNO_MULT_MAX, Math.max(INTERNO_MULT_MIN, multVolume * fatorFidelidade * multDesconto));
}

// ── Bônus de prospecção ativa ────────────────────────────────────────────
// +10 pontos percentuais TRAVADOS: somados DEPOIS do multiplicador, nunca
// dentro dele. Decisão explícita do dono do produto (09/09/2026): "os
// multiplicadores não podem inferir efeito sobre esse percentual — ele é
// travado nos 10% adicionais independentemente do que acontecer". Não é 10%
// relativo (isso daria +1,25 p.p. sobre uma base de 12,5%) — são 10 pontos
// percentuais cheios (12,5% vira 22,5%).
export const INTERNO_BONUS_PROSPECCAO_PP = 10;

/** Precisa bater o valor exato de "Prospecção ativa" em StepBasicInfo.tsx
 *  (ORIGEM_OPTIONS) — só ignora maiúscula/minúscula, nunca "contém". */
export function veioDeProspeccaoAtiva(origem: string | null | undefined): boolean {
  return !!origem && origem.trim().toLowerCase() === 'prospecção ativa';
}

export interface ResultadoComissaoInterno {
  base: number;
  multVolume: number;
  fatorFidelidade: number;
  multDesconto: number;
  multFinal: number;
  bonusProspeccaoPp: number;
  /** % final desta negociação: base × multFinal, mais o bônus de prospecção por fora. */
  percentualFinal: number;
}

export function calcularPercentualInterno(params: {
  descTarifa: number;
  descBandeira: number;
  fidelidade: string | null | undefined;
  origem: string | null | undefined;
  pctAtingimentoMeta: number;
}): ResultadoComissaoInterno {
  const base = getComissaoInternoTabela(params.descTarifa, params.descBandeira);
  const multVolume = getInternoMultiplicadorVolume(params.pctAtingimentoMeta);
  const fatorFidelidade = getFatorFidelidade(params.fidelidade, undefined);
  const multDesconto = getInternoMultiplicadorDesconto(params.descTarifa);
  const multFinal = getInternoMultiplicadorFinal(multVolume, fatorFidelidade, multDesconto);
  const bonusProspeccaoPp = veioDeProspeccaoAtiva(params.origem) ? INTERNO_BONUS_PROSPECCAO_PP : 0;
  return {
    base,
    multVolume,
    fatorFidelidade,
    multDesconto,
    multFinal,
    bonusProspeccaoPp,
    percentualFinal: base * multFinal + bonusProspeccaoPp,
  };
}

// ── Bônus fixo de SLA ────────────────────────────────────────────────────
// Pago 1x no fechamento do mês (não por negociação) quando a MÉDIA mensal do
// SLA útil da vendedora (mesma conta de views/CRM/Analytics/blocks, relocada
// para src/lib/slaRanking.ts) fica dentro do limiar. Decisão do dono do
// produto (09/09/2026): média do mês, não atendimento individual.
export const INTERNO_SLA_LIMIAR_MIN = 7;
export const INTERNO_SLA_BONUS_RS = 100;

// ==========================================
// RESOLUÇÃO POR NEGOCIAÇÃO (função pura — a parte que toca banco é
// src/lib/comissaoInternaV1.ts)
// ==========================================

export interface NegociacaoInternaMes {
  id: number;
  responsavel_id?: string | null;
  consumo_medio_kwh: number | null;
  fidelidade?: string | null;
  origem?: string | null;
  unidades_consumidoras?: Array<{
    grupo_tarifario?: string | null;
    desconto_tarifa?: number | null;
    desconto_bandeira?: number | null;
  }> | null;
  /** Quem do pós-venda gerou a venda por indicação (#6236): comissão 120%, metade para cada um. */
  pos_venda_id?: string | null;
}

/**
 * Resolve o percentual de comissão de UMA negociação a partir da 1ª UC dela —
 * mesmo padrão do PlanModal (`ucs[0]`): a condição é gravada por UC, mas o
 * modal Alterar Plano do Comercial Interno aplica o mesmo desconto a todas as
 * UCs de uma negociação, então a primeira já representa a condição inteira.
 *
 * UC grava desconto como fração (0,22 = 22%) — por isso o × 100. Retorna null
 * quando a UC não tem desconto_tarifa gravado (negociação sem plano definido
 * ainda): o chamador decide o que fazer (hoje: ignora e registra no log de
 * auditoria do fechamento, não inventa um percentual).
 */
export function resolverPercentualNegociacaoInterna(
  neg: NegociacaoInternaMes,
  pctAtingimentoMeta: number
): { descTarifa: number; descBandeira: number; resultado: ResultadoComissaoInterno } | null {
  const uc = neg.unidades_consumidoras?.[0];
  if (!uc || uc.desconto_tarifa == null) return null;

  const descTarifa = Math.round((Number(uc.desconto_tarifa) || 0) * 100);
  const descBandeira = Math.round((Number(uc.desconto_bandeira) || 0) * 100);
  const resultado = calcularPercentualInterno({
    descTarifa,
    descBandeira,
    fidelidade: neg.fidelidade,
    origem: neg.origem,
    pctAtingimentoMeta,
  });

  return { descTarifa, descBandeira, resultado };
}

// ==========================================
// COMISSÃO DAS NEGOCIAÇÕES DO MÊS (função pura)
// ==========================================
// Fonte única da conta por negociação: o fechamento da Folha
// (comissaoInternaV1.calcularFechamentoInternoV1) e a barra "Meu Trabalho" do
// CRM chamam esta mesma função. O que fica de fora daqui — salário e bônus de
// SLA — só existe no fechamento.

const round2 = (v: number) => Math.round(v * 100) / 100;

export interface DetalheNegociacaoInterna {
  negociacao_id: number;
  consumo_medio_kwh: number;
  desconto_tarifa: number;
  desconto_bandeira: number;
  is_grupo_a: boolean;
  grupo_a_status?: GrupoAStatus;
  percentual_base: number;
  mult_volume: number;
  fator_fidelidade: number;
  mult_desconto: number;
  mult_final: number;
  bonus_prospeccao_pp: number;
  percentual_final: number;
  /** Parte de quem vendeu (com indicação do pós-venda, 60% da normal). */
  valor_comissao: number;
  /** Indicação do pós-venda: a comissão normal (100%) e quem do pós-venda fica com a outra metade. */
  valor_normal?: number;
  pos_venda_id?: string | null;
}

export interface ComissoesInternoMes {
  volumeTotal: number;
  pctAtingimentoMeta: number;
  /** Negociações resolvidas (com valor) seguidas das de Grupo A (valor 0). */
  detalhes: DetalheNegociacaoInterna[];
  /** Memória de cálculo por negociação — vai para detalhe_calculo.negociacoes. */
  memoria: Array<Record<string, unknown>>;
  /** Sem desconto_tarifa gravado na UC: plano não definido, fica sem comissão. */
  ignoradas: Array<{ negociacao_id: number; motivo: string }>;
  grupoA: number[];
  /** Soma das comissões de quem vendeu (cada negociação já arredondada em centavos). */
  valorComissaoTotal: number;
  /** Vendas indicadas pelo pós-venda: a parte que vai para ele (#6236). */
  repassesPosVenda: RepassePosVenda[];
}

/**
 * Comissão de cada negociação aprovada no mês: kWh × tarifa × % final da
 * tabela. Grupo A fica fora do cálculo e do volume (condição negociada
 * manualmente pelo gerente). Sem meta configurada → 0% de atingimento (pior
 * faixa do pilar Volume), nunca a meta do vendedor externo.
 */
export function calcularComissoesInterno(
  negs: Array<NegociacaoInternaMes & { is_grupo_a: boolean }>,
  metaKwh: number,
  tarifaValor: number
): ComissoesInternoMes {
  const negsNormais = negs.filter(n => !n.is_grupo_a);
  const negsGrupoA = negs.filter(n => n.is_grupo_a);

  const volumeTotal = negsNormais.reduce((s, n) => s + (Number(n.consumo_medio_kwh) || 0), 0);
  const pctAtingimentoMeta = metaKwh > 0 ? (volumeTotal / metaKwh) * 100 : 0;

  const detalhes: DetalheNegociacaoInterna[] = [];
  const memoria: Array<Record<string, unknown>> = [];
  const ignoradas: Array<{ negociacao_id: number; motivo: string }> = [];
  const repassesPosVenda: RepassePosVenda[] = [];
  let valorComissaoTotal = 0;

  for (const neg of negsNormais) {
    const kwh = Number(neg.consumo_medio_kwh) || 0;
    const resolvido = resolverPercentualNegociacaoInterna(neg, pctAtingimentoMeta);
    if (!resolvido) {
      ignoradas.push({ negociacao_id: neg.id, motivo: 'sem desconto_tarifa gravado na UC — plano não definido' });
      continue;
    }
    const { descTarifa, descBandeira, resultado } = resolvido;
    const valorNormal = round2(kwh * tarifaValor * (resultado.percentualFinal / 100));
    // Venda indicada pelo pós-venda: 120% da normal, metade para quem vendeu, metade para ele.
    const repasse = neg.pos_venda_id ? montarRepasse(neg.id, neg.pos_venda_id, kwh, valorNormal) : null;
    if (repasse) repassesPosVenda.push(repasse);
    const valor = repasse ? repasse.parte_vendedor : valorNormal;
    valorComissaoTotal += valor;

    detalhes.push({
      negociacao_id: neg.id,
      consumo_medio_kwh: kwh,
      desconto_tarifa: descTarifa,
      desconto_bandeira: descBandeira,
      is_grupo_a: false,
      percentual_base: resultado.base,
      mult_volume: resultado.multVolume,
      fator_fidelidade: resultado.fatorFidelidade,
      mult_desconto: resultado.multDesconto,
      mult_final: resultado.multFinal,
      bonus_prospeccao_pp: resultado.bonusProspeccaoPp,
      percentual_final: resultado.percentualFinal,
      valor_comissao: valor,
      ...(repasse ? { valor_normal: valorNormal, pos_venda_id: repasse.pos_venda_id } : {}),
    });
    memoria.push({
      negociacao_id: neg.id, kwh, desconto_tarifa: descTarifa, desconto_bandeira: descBandeira, ...resultado, valor,
      ...(repasse ? { indicacao_pos_venda: repasse } : {}),
    });
  }

  for (const neg of negsGrupoA) {
    const uc = neg.unidades_consumidoras?.[0];
    detalhes.push({
      negociacao_id: neg.id,
      consumo_medio_kwh: Number(neg.consumo_medio_kwh) || 0,
      desconto_tarifa: Math.round((Number(uc?.desconto_tarifa) || 0) * 100),
      desconto_bandeira: Math.round((Number(uc?.desconto_bandeira) || 0) * 100),
      is_grupo_a: true,
      grupo_a_status: 'pendente_aprovacao_gerente',
      percentual_base: 0,
      mult_volume: 0,
      fator_fidelidade: 0,
      mult_desconto: 0,
      mult_final: 0,
      bonus_prospeccao_pp: 0,
      percentual_final: 0,
      valor_comissao: 0,
    });
  }

  return {
    volumeTotal,
    pctAtingimentoMeta,
    detalhes,
    memoria,
    ignoradas,
    grupoA: negsGrupoA.map(n => n.id),
    valorComissaoTotal,
    repassesPosVenda,
  };
}
